import { useDiaryConsents } from "@/features/consent/hooks/useDiaryConsents";
import { selectProfileByIdQueryOptions } from "@/features/profile/api/selectProfile";
import { useDiaryCopy } from "@/features/i18n/diaryCopy";
import { useJourneyCopy } from "@/features/i18n/journeyCopy";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { getPatientReferenceDateLabel } from "@/features/diaries/utils/getReferenceDateFieldCopy";
import { useProfileCopy } from "@/features/i18n/profileCopy";
import { useAuth } from "@/utils/auth";
import { useQuery } from "@tanstack/react-query";

import type { StatefulDiaryT } from "@/types/database/diaries";
import {
    arePriorDiaryPhasesOutstanding,
    buildConsentTodos,
    buildFormsTodos,
    buildOnboardingTodos,
    buildSetupTodos,
    type JourneyTodos,
    type OnboardingProfile,
} from "../journeyTodos";

/**
 * Global "things to do" for the patient journey, keyed by phase. Consolidates the
 * checks already used elsewhere (header profile badge, setup gating, consent
 * gating, forms to-do) into one structure that drives the journey progress bar.
 */
export function useJourneyTodos(diary?: StatefulDiaryT | null): JourneyTodos {
    const auth = useAuth();
    const profileCopy = useProfileCopy();
    const journeyCopy = useJourneyCopy();
    const diaryCopy = useDiaryCopy();
    const { locale } = useAppLocale();
    const { data: profile } = useQuery(selectProfileByIdQueryOptions(auth.id));
    const { consents: latestConsents } = useDiaryConsents(diary);

    const onboarding = buildOnboardingTodos(profile as OnboardingProfile | null, {
        name: profileCopy.name,
        dateOfBirth: profileCopy.dateOfBirth,
        email: profileCopy.email,
        phone: profileCopy.phone,
        organisation: profileCopy.organisation,
    });

    const authCtx = { roles: auth.roles };
    const consentSource = latestConsents;
    const setup = diary
        ? buildSetupTodos(diary, {
              hospitalNumber: journeyCopy.setupHospitalNumber,
              procedureDate: getPatientReferenceDateLabel(diary, diaryCopy, locale),
              side: journeyCopy.setupSide,
          })
        : [];
    const consent = diary ? buildConsentTodos(consentSource, auth.id) : [];
    const priorPhasesOutstanding = arePriorDiaryPhasesOutstanding({ setup, consent });

    return {
        onboarding,
        setup,
        consent,
        forms: diary ? buildFormsTodos(diary, authCtx, { priorPhasesOutstanding }) : [],
    };
}
